import type { FunctionComponent } from 'react';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { NewStudentTutorDataInterface } from '@whizz/api-model/dist/@types';
import type { StudentAssessmentStateType } from '../types';
import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { GlobalDashboardLayout } from '../../layouts';
import { Alert, ChoiceGroup, Table } from '../../components';
import { createGlobalStyle } from 'styled-components';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../util/vars';
import { Continue } from '../../components/selection-action/SelectionActionControl';
import { useModal } from '../../hooks/useModal';
import { ModalFooter } from '../../components/modal/Modal';
import { CancelButtonModal, SaveButton } from '../../global';
import { repository } from '@whizz/api-client';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useClassSelector } from '../../hooks/filters/useClassSelector';
import { useIdentity } from '@whizz/react-identity';
import { ResetAssessmentCheckBox, StudentStatus, statuses } from './ResetAssessments.components';
import { useSuccessErrorModal } from '../../hooks/useSuccessErrorModal';
import NoClasses from '../../global/sections/NoClasses';
import classNames from 'classnames';
import { DateTime } from 'luxon';

type StudentType = {
  id: number,
  name: string,
  status: StudentAssessmentStateType,
  assessment_percentage: number | null,
  maths_age_now: number | null,
  days_since_assessment: number | null,
};

const sortBy = {
  column: 'status',
  desc: false,
};

const TABLE_ID = 'reset-assessments';

const GlobalStyle = createGlobalStyle`
  #${TABLE_ID} {
    [data-id="col_assessment_percentage"],
    [data-id="col_maths_age_now"] {
      width: 0;
    }
    [data-column="cell_reset"] label {
      white-space: nowrap;
    }
    [data-column="cell_assessment_percentage"],
    [data-column="cell_maths_age_now"] {
      text-align: center;
    }
  }
`;

const mapData = (students: NewStudentTutorDataInterface[] | undefined): StudentType[] | undefined => {
  if (students) {
    return students.map((student): StudentType => ({
      id: student.id,
      name: `${student.firstName} ${student.lastName}`,
      assessment_percentage: student.percentageCompletedLastAssessment,
      maths_age_now: student.mathsAge,
      days_since_assessment: student.daysSinceAssessment,
      status: student.lastLogonDate ? student.reassessmentEndDate ? 'complete' : student.reassessmentStartDate ? 'started' : 'not_started' : 'not_logged_in',
    }));
  }
};

const ResetAssessments: FunctionComponent = () => {
  const { translate } = useI18n();
  const { me } = useIdentity();

  const teacherId = (me as Teacher).getId();

  const [ resetIds, setResetIds ] = useState<StudentType['id'][]>([]);
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const queryClient = useQueryClient();

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
    },
  );

  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'space-between');

  const studentsQuery = useQuery(
    [ QUERIES_IDS.GROUP_STUDENTS, classValue?.value ],
    () => repository.GroupReportRepository.oneByGroupId(classValue?.value),
    {
      enabled: null != classValue,
    },
  );

  useEffect(() => {
    setResetIds([]);
  }, [ classValue?.value ]);

  const students = mapData(studentsQuery.data?.students);

  const resetAssessmentMutation = useMutation(
    ({ selectedIds }: { selectedIds: number[] }) => repository.StudentRepository.resetAssessment(selectedIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERIES_IDS.GROUP_STUDENTS);
        setResetIds([]);
        onSuccess();
      },
      onError: () => {
        onError();
      },
    },
  );

  const [ continueActionModal, toggleContinueActionModal, setContinueActionModalProps ] = useModal(
    ({ students, onConfirm }: { students:  StudentType[], onConfirm: (id: number[]) => void }) => {
      const [ confirm, setConfirm ] = useState(false);
      return (
        <>
          <div className={'container'}>
            <div className={'space-y-4'}>
              <p>{translate('pages.RESET_ASSESSMENTS.INFO.HEADER')}</p>
              <ul className={'list-disc list-outside inline-block ml-lg space-y-2'}>
                <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE1')}</li>
                <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE2')}</li>
                <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE3')}</li>
              </ul>
              <p>{translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.IF_YOU_PROCEED')}</p>
              <p>{translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.ARE_YOU_SURE', { count: students.length })}</p>
              <div className={'overflow-x-auto overflow-y-hidden p-sm'}>
                <ul className={'list-disc list-inside inline-block min-w-full font-bold'} style={{ columnCount: Math.ceil(students.length / 10) }}>
                  {students.map((student, index) => (<li key={index} className={'whitespace-nowrap p-1'}>{student.name}</li>))}
                </ul>
              </div>
              <h2 className={'text-error font-bold'}>{translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.IRREVERSIBLE')}</h2>
              <ChoiceGroup id={'type-to-confirm'} label={translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.WARNING')} onChange={() => setConfirm((prev) => !prev)} checked={confirm} />
            </div>
          </div>
          <ModalFooter color={'error'}>
            <CancelButtonModal
              align={'start'}
              onClick={() => toggleContinueActionModal(false)}
            />
            <SaveButton
              align={'start'}
              icon={GLOBAL_ICONS.checkbox}
              showConfirmLabel
              disabled={!confirm}
              onClick={() => {
                onConfirm(students.map((student) => student.id));
                toggleContinueActionModal(false);
              }}
            >{translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.confirm')}</SaveButton>
          </ModalFooter>
        </>
      );
    }, { title: translate('pages.RESET_ASSESSMENTS.MODALS.CONFIRM.HEADER'), color: 'error' });

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  const onClick = (id: number) => () => {
    if (resetIds.includes(id)) {
      setResetIds((state) => state.filter((n) => n !== id));
    } else {
      setResetIds((state) =>  [ ...state, id ]);
    }
  };

  const onClickAll = () => {
    if (resetIds.length === 0 && students) {
      setResetIds(students.map((student) => student.id));
    } else {
      setResetIds([]);
    }
  };

  const onContinueWithSelected = (students: StudentType[]) => {
    toggleContinueActionModal(true);
    setContinueActionModalProps((prev) => ({
      ...prev,
      students: students,
      disableMaskClick: true,
      onConfirm: (studentsIds: number[]) => resetAssessmentMutation.mutate({ selectedIds: studentsIds }),
    }));
  };

  const columns = [
    {
      id: 'reset',
      Header: <ResetAssessmentCheckBox id={'all'} label={translate('pages.RESET_ASSESSMENTS.TABLE.SELECT_ALL')} checked={students && resetIds.length === students.length} onClick={onClickAll} />,
      accessor: 'reset',
      disableSortBy: true,
      Cell: ({ row }: { row: any }) => <ResetAssessmentCheckBox id={row.original.id} label={row.original.name} checked={resetIds.includes(row.original.id)} onClick={onClick(row.original.id)} />,
    },
    {
      id: 'status',
      Header: translate('pages.RESET_ASSESSMENTS.TABLE.STATUS'),
      accessor: 'status',
      Cell: ({ row, value }: { row: any, value: StudentAssessmentStateType }) => <StudentStatus status={value} days={row.original.days_since_assessment} />,
      sortType: (a: any, b: any) => {
        const statusA = statuses[a.values.status as StudentAssessmentStateType].rank;
        const statusB = statuses[b.values.status as StudentAssessmentStateType].rank;
        if (statusA === statusB) {
          const daysA = a.original.days_since_assessment;
          const daysB = b.original.days_since_assessment;
          return daysB - daysA;
        }
        return statusA - statusB;
      },
    },
    {
      id: 'assessment_percentage',
      Header: translate('pages.RESET_ASSESSMENTS.TABLE.PROGRESS'),
      accessor: 'assessment_percentage',
    },
    {
      id: 'maths_age_now',
      Header: translate('pages.RESET_ASSESSMENTS.TABLE.MATHS_AGE_NOW'),
      accessor: 'maths_age_now',
    },
  ];

  const showNoClasses = classesQuery.isFetched && 0 === classesQuery.data?.length;
  const isLoading = studentsQuery.isFetching;
  const showContinue = !!resetIds.length;

  return (
    <GlobalDashboardLayout
      isLoading={isLoading}
      title={translate('pages.RESET_ASSESSMENTS.HEADER')}
      actionContent={classSelector}
      classId={classValue?.value}
      disableContainer={!classValue}
    >
      <GlobalStyle />
      {successErrorModal}
      {continueActionModal}
      {!classValue ? (
        showNoClasses
          ? <NoClasses />
          : <Alert show>{translate('pages.HOME.SELECT_CLASS')}</Alert>
      ) : null
      }
      {classValue ? (
        <div>
          <Alert show className='mb-md space-y-4'>
            <p>{translate('pages.RESET_ASSESSMENTS.INFO.HEADER')}</p>
            <ul className={'list-inside list-disc space-y-2 ml-md'}>
              <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE1')}</li>
              <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE2')}</li>
              <li>{translate('pages.RESET_ASSESSMENTS.INFO.LINE3')}</li>
            </ul>
          </Alert>
          <div className={classNames({ 'mb-32': showContinue })}>
            <Table
              id={TABLE_ID}
              cols={columns}
              content={students ?? []}
              initialState={{
                sortBy: [
                  {
                    id: sortBy.column,
                    desc: sortBy.desc,
                  },
                ],
              }}
            />
          </div>
          {showContinue ? (
            <Continue
              onClick={() => students ? onContinueWithSelected(students.filter((student) => resetIds.includes(student.id))) : null}
              count={resetIds.length}
              isLoading={resetAssessmentMutation.isLoading}
            />
          ) : null}
        </div>
      ) : null}
    </GlobalDashboardLayout>
  );
};

export default ResetAssessments;
